/**
 * Route file used by app.server and Layout.js
 * The main route of this site.
 *
 * more information: https://reacttraining.com/react-router/web/example/route-config
 */
import _ from 'lodash'
import dashboardRoutes from '../../../conf/dashboardConf'
import Dashboard, { MobileDashboard } from '../../app/containers/Dashboard'
import Warehouse from '../../app/containers/Warehouse'
import Item from '../../app/containers/Item'
import SecretDashboard from '../../app/containers/SecretDashboard'
import BatchProposal from '../../app/containers/BatchProposal'
import ListingQuery from '../../app/containers/Listing/QueryPage'
import ErrorPage from '../../app/containers/ErrorPage'
import ListingClone from '../../app/containers/External/Listing/Clone'

import ExpiringItems from '../../app/components/Warehouse/ExpiringItems'
import UnsalableItems from '../../app/components/Warehouse/UnsalableItems'
import BestSelling from '../../app/components/Item/BestSelling'
import SearchKeywords from '../../app/components/Item/SearchKeywords'
import SalesPerformance from '../../app/components/Item/SalesPerformance'
import CustomerInterest from '../components/Item/CustomerInterest'

import NoPrivilegedPermission from '../../app/components/Common/NoPrivilegedPermission'
import UpdateListingNoPrivilegedPermission from '../../app/components/Common/NoPrivilegedPermission/updateListingInfo'
import {
  privilegedPagesProtected,
  privilegedOrWithinFallbackPath,
  withSupplierPermission
} from '../lib/authWrapper'

import { SUB_ANCHOR_MAP } from '../components/Dashboard/constants'

/**
 * Proposal
 */
import ProposalRoutes from '../../app/components/Proposal/Routes'
import { PROPOSAL_PHASE_TYPE } from '../constants/proposalTypes'
import ListingPreview from '../../app/containers/Proposal/ListingPreview'
import UpdateListingPreview from '../../app/containers/Proposal/UpdateListingPreview'
import NewListing from '../../app/containers/Proposal/NewListing'
import NewProduct from '../../app/containers/Proposal/NewProduct'
// 修改賣場詳情
import UpdateCopy from '../../app/containers/Proposal/UpdateCopy'
// 修改賣場影片
import UpdateVideo from '../../app/containers/Proposal/UpdateVideo'
// 修改賣場圖片
import UpdateImage from '../../app/containers/Proposal/UpdateImage'
// 修改賣場規格表
import UpdateAttribute from '../../app/containers/Proposal/UpdateAttribute'
// 調整賣場屬性項目
import UpdateListingModel from '../../app/containers/Proposal/UpdateListingModel'
// 修改賣場資訊
import UpdateListingInfo from '../../app/containers/Proposal/UpdateListingInfo'
// 批次修改賣場圖片
import BatchUpdateImage from '../../app/containers/BatchProposal/UpdateImage'
// 批次修改賣場影片
import BatchUpdateVideo from '../../app/containers/BatchProposal/UpdateVideo'
// 批次修改賣場詳情與保證
import BatchUpdateCopy from '../../app/containers/BatchProposal/UpdateCopy'
// 批次修改賣場規格表
import BatchUpdateAttribute from '../../app/containers/BatchProposal/UpdateAttribute'
// 批次上傳賣場圖片
import BatchUploadImage from '../../app/containers/BatchProposal/BatchUploadImage'
// 批次修改賣場提案查詢
import BatchProposalQuery from '../containers/BatchProposal/QueryPage'
// 批次上傳賣場圖片檢視頁
import BatchUpdateImageOverview from '../../app/containers/BatchProposal/UpdateImageOverview'
// 批次發送折價券
import BatchCouponDispatch from '../../app/containers/BatchProposal/CouponDispatch'
// 檢核關鍵字查詢
import KeywordQuery from '../../app/containers/Keyword/Query'
// Seller Dashboard
import SellerHome from '../containers/Seller'
import SellerLogin from '../containers/Seller/Login'
import SellerLogout from '../containers/Seller/Logout'
import SellerDashboard from '../containers/Seller/Dashboard'
import SellerError from '../containers/Seller/Error'
import SellerItemsBestSellerRanking from '../containers/Seller/Items/BestsellerRanking'
import SellerItemsSalesPerformance from '../containers/Seller/Items/SalesPerformance'

// test
import Test from '../../app/containers/Test'

import { PROPOSAL_TYPE as OLD_PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'

const isDevEnv = _.get(global, 'window.EC_APP_ENV') === 'development'

const PROPOSAL_TYPE = {
  ...OLD_PROPOSAL_TYPE,
  UPDATE_LISTING: 'updateListing'
}

const withPrivilegedAndSupplierPermission = (fallbackPaths, allPathsMatched) => {
  return (Component, FailedComponent) => {
    return _.flow(
      (Component, FailedComponent) => privilegedOrWithinFallbackPath(Component, fallbackPaths, allPathsMatched, FailedComponent),
      withSupplierPermission
    )(Component, FailedComponent)
  }
}

const BATCH_PHASES = [
  '',
  PROPOSAL_PHASE_TYPE.COMPOSE,
  PROPOSAL_PHASE_TYPE.OVERVIEW
]

/**
 * make sure to put `exact: true` if it's a exact path to match
 * flag name is required because server uses it to expose entries
 */
const routes = [
  {
    pageKey: 'Warehouse',
    path: '/warehouse',
    component: Warehouse,
    fetchData: Warehouse.fetchData,
    routes: [
      {
        flagName: 'features.sp_expItems._enable',
        pageKey: 'expiringItems',
        exact: true,
        path: '/warehouse/expiringItems',
        component: privilegedPagesProtected({
          Component: ExpiringItems,
          FailureComponent: NoPrivilegedPermission
        }),
        text: '效期警示品項清單',
        icon: 'market-status'
      },
      {
        flagName: 'features.sp_unsaItems._enable',
        pageKey: 'unsalableItems',
        exact: true,
        path: '/warehouse/unsalableItems',
        component: privilegedPagesProtected({
          Component: UnsalableItems,
          FailureComponent: NoPrivilegedPermission
        }),
        text: '滯銷品項清單',
        icon: 'market-status'
      },
      {
        path: '/dashboard',
        text: '回營運看板',
        icon: 'market-status'
      }
    ]
  },
  {
    pageKey: 'Item',
    path: '/item',
    name: '賣場與商品',
    component: Item,
    fetchData: Item.fetchData,
    routes: [
      {
        pageKey: 'salesPerformance',
        exact: true,
        path: '/item/salesPerformance',
        component: SalesPerformance,
        text: '即時業績'
      },
      {
        pageKey: 'bestSelling',
        exact: true,
        path: '/item/bestSelling',
        component: BestSelling,
        text: '熱銷賣場'
      },
      ...(Object.values(SUB_ANCHOR_MAP).map(custInterestTab => ({
        pageKey: `customerInterest_${custInterestTab.link}`,
        exact: true,
        path: `/item/:statsType(${custInterestTab.link})`,
        component: CustomerInterest,
        text: custInterestTab.linkTitle,
        statsType: custInterestTab.link
      }))),
      {
        pageKey: 'searchKeywords',
        exact: true,
        path: '/item/searchKeywords',
        component: SearchKeywords,
        text: '站內熱門關鍵字'
      },
      {
        path: '/dashboard',
        text: '回營運看板'
      }
    ]
  },
  {
    pageKey: 'SecretDashboard',
    exact: true,
    path: '/371',
    component: withSupplierPermission(SecretDashboard),
    fetchData: SecretDashboard.fetchData
  },
  {
    pageKey: 'BatchProposal',
    exact: true,
    path: '/proposal/batch',
    name: '批次提案工具',
    component: BatchProposal
  },
  {
    pageKey: 'ListingQuery',
    exact: true,
    path: '/listing/query',
    name: '查詢賣場',
    component: withSupplierPermission(ListingQuery),
    fetchData: ListingQuery.fetchData
  },
  {
    pageKey: 'BatchCoupon',
    exact: true,
    path: '/batchCoupon/dispatch',
    name: '批次發送折價券',
    component: privilegedPagesProtected({
      Component: BatchCouponDispatch,
      FailureComponent: NoPrivilegedPermission
    }),
    fetchData: BatchCouponDispatch.fetchData
  },
  {
    pageKey: 'BatchUploadImage',
    exact: true,
    path: '/batchUpload/updateImage',
    name: '批次上傳賣場圖片',
    component: BatchUploadImage,
    fetchData: BatchUploadImage.fetchData
  },
  {
    pageKey: 'BatchUploadImageOverview',
    exact: true,
    path: `/batchUpload/:feature(${PROPOSAL_TYPE.UPDATE_IMAGE})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
    name: '批次上傳賣場圖片檢視頁',
    component: BatchUpdateImageOverview
  },
  {
    pageKey: 'Proposal',
    name: '賣場提案',
    path: `/proposal/:feature(${Object.values(PROPOSAL_TYPE).join('|')})`,
    component: ProposalRoutes,
    routes: [
      /** newListing */
      {
        pageKey: `${PROPOSAL_TYPE.NEW_LISTING}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '提案內容填寫',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission()(NewListing)
      },
      {
        pageKey: `${PROPOSAL_TYPE.NEW_LISTING}-${PROPOSAL_PHASE_TYPE.CLONE}`,
        text: '複製提案',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.CLONE})`,
        component: withPrivilegedAndSupplierPermission(['newListingCompose'])(NewListing)
      },
      {
        pageKey: `${PROPOSAL_TYPE.NEW_LISTING}-${PROPOSAL_TYPE.CLONE_LISTING}`,
        text: '用賣場複製提案',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.CLONE_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['newListingCompose'])(NewListing)
      },
      {
        pageKey: `${PROPOSAL_TYPE.NEW_LISTING}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '提案檢視頁',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(NewListing)
      },
      {
        pageKey: `${PROPOSAL_TYPE.NEW_LISTING}-${PROPOSAL_PHASE_TYPE.PREVIEW}`,
        text: '提案預覽賣場',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.PREVIEW})`,
        component: privilegedOrWithinFallbackPath(ListingPreview, ['newListingCompose'])
      },
      /** newProduct */
      {
        pageKey: `${PROPOSAL_TYPE.NEW_PRODUCT}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '提案內容填寫',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_PRODUCT})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE}|${PROPOSAL_PHASE_TYPE.CLONE})`,
        component: withPrivilegedAndSupplierPermission(['newProductCompose'])(NewProduct)
      },
      {
        pageKey: `${PROPOSAL_TYPE.NEW_PRODUCT}-${PROPOSAL_PHASE_TYPE.CLONE}`,
        text: '複製提案',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_PRODUCT})/:phase(${PROPOSAL_PHASE_TYPE.CLONE})`,
        component: withPrivilegedAndSupplierPermission(['newProductCompose'])(NewProduct)
      },
      {
        pageKey: `${PROPOSAL_TYPE.NEW_PRODUCT}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '提案檢視頁',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.NEW_PRODUCT})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(NewProduct)
      },
      /** updateVideo */
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_VIDEO}`,
        text: '修改賣場影片 提案新增',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_VIDEO})`,
        component: withPrivilegedAndSupplierPermission(['updateVideo'])(UpdateVideo)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_VIDEO}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '修改賣場影片 提案編輯',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_VIDEO})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['updateVideo'])(UpdateVideo)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_VIDEO}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '修改賣場影片 提案檢視',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_VIDEO})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(UpdateVideo)
      },
      /** updateCopy */
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_COPY}`,
        text: '修改賣場詳情與商品保證 提案新增',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_COPY})`,
        component: withPrivilegedAndSupplierPermission(['scmUpdateCopyProposal', 'updateCopy'])(UpdateCopy)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_COPY}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '修改賣場詳情與商品保證 提案編輯',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_COPY})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['scmUpdateCopyProposal', 'updateCopy'])(UpdateCopy)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_COPY}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '修改賣場詳情與商品保證 提案檢視',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_COPY})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(UpdateCopy)
      },
      /** updateImage */
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_IMAGE}`,
        text: '修改賣場圖片 提案新增',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_IMAGE})`,
        component: withPrivilegedAndSupplierPermission(['scmUpdateImage', 'updateImage'])(UpdateImage)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_IMAGE}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '修改賣場圖片 提案編輯',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_IMAGE})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['scmUpdateImage', 'updateImage'])(UpdateImage)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_IMAGE}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '修改賣場圖片 提案檢視',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_IMAGE})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(UpdateImage)
      },
      /** UpdateAttribute */
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_ATTRIBUTE}`,
        text: '修改賣場規格表 提案新增',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_ATTRIBUTE})`,
        component: withPrivilegedAndSupplierPermission(['updateAttribute'])(UpdateAttribute)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_ATTRIBUTE}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '修改賣場規格表 提案編輯',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_ATTRIBUTE})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['updateAttribute'])(UpdateAttribute)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_ATTRIBUTE}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '修改賣場規格表 提案檢視',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_ATTRIBUTE})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(UpdateAttribute)
      },
      /** UpdateListingModel */
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING_MODEL}`,
        text: '調整賣場屬性項目 提案新增',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING_MODEL})`,
        component: withPrivilegedAndSupplierPermission(['updateListingModel'])(UpdateListingModel)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING_MODEL}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '調整賣場屬性項目 提案編輯',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING_MODEL})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['updateListingModel'])(UpdateListingModel)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING_MODEL}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '調整賣場屬性項目 提案檢視',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING_MODEL})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(UpdateListingModel)
      },
      /** UpdateListing */
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING}`,
        text: '修改賣場資訊 提案新增',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING})`,
        component: withPrivilegedAndSupplierPermission(['updateListing', 'updateImage', 'updateAttribute', 'updateVideo', 'updateCopy'], true)(UpdateListingInfo, UpdateListingNoPrivilegedPermission)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING}-${PROPOSAL_PHASE_TYPE.COMPOSE}`,
        text: '修改賣場資訊 提案編輯',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.COMPOSE})`,
        component: withPrivilegedAndSupplierPermission(['updateListing', 'updateImage', 'updateAttribute', 'updateVideo', 'updateCopy'], true)(UpdateListingInfo, UpdateListingNoPrivilegedPermission)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING}-${PROPOSAL_PHASE_TYPE.OVERVIEW}`,
        text: '修改賣場資訊 提案檢視',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.OVERVIEW})`,
        component: withPrivilegedAndSupplierPermission(['scmProposalQuery'])(UpdateListingInfo, UpdateListingNoPrivilegedPermission)
      },
      {
        pageKey: `${PROPOSAL_TYPE.UPDATE_LISTING}-${PROPOSAL_PHASE_TYPE.PREVIEW}`,
        text: '修改預覽賣場',
        exact: true,
        path: `/proposal/:feature(${PROPOSAL_TYPE.UPDATE_LISTING})/:phase(${PROPOSAL_PHASE_TYPE.PREVIEW})`,
        component: privilegedOrWithinFallbackPath(UpdateListingPreview, ['updateListing'])
      }
    ]
  },
  {
    pageKey: 'BatchProposal',
    name: '批次賣場提案',
    path: `/proposal/batch/:feature(${Object.values(PROPOSAL_TYPE).join('|')}|query)`,
    component: ProposalRoutes,
    routes: [
      // updateImage
      ...BATCH_PHASES.map(phase => ({
        pageKey: `${PROPOSAL_TYPE.UPDATE_IMAGE}-${phase}`,
        text: '批次修改賣場圖片 提案編輯',
        exact: true,
        path: `/proposal/batch/:feature(${PROPOSAL_TYPE.UPDATE_IMAGE})/:phase(${phase})`,
        component: withPrivilegedAndSupplierPermission(['scmUpdateImage', 'updateImage'])(BatchUpdateImage)
      })),
      // updateVideo
      ...BATCH_PHASES.map(phase => ({
        pageKey: `${PROPOSAL_TYPE.UPDATE_VIDEO}-${phase}`,
        text: '批次修改賣場影片 提案編輯',
        exact: true,
        path: `/proposal/batch/:feature(${PROPOSAL_TYPE.UPDATE_VIDEO})/:phase(${phase})`,
        component: withPrivilegedAndSupplierPermission(['updateVideo'])(BatchUpdateVideo)
      })),
      // updateCopy
      ...BATCH_PHASES.map(phase => ({
        pageKey: `${PROPOSAL_TYPE.UPDATE_COPY}-${phase}`,
        text: '批次修改賣場詳情與保證 提案編輯',
        exact: true,
        path: `/proposal/batch/:feature(${PROPOSAL_TYPE.UPDATE_COPY})/:phase(${phase})`,
        component: withPrivilegedAndSupplierPermission(['scmUpdateCopyProposal', 'updateCopy'])(BatchUpdateCopy)
      })),
      // updateAttribute
      ...BATCH_PHASES.map(phase => ({
        pageKey: `${PROPOSAL_TYPE.UPDATE_ATTRIBUTE}-${phase}`,
        text: '批次修改賣場規格表 提案編輯',
        exact: true,
        path: `/proposal/batch/:feature(${PROPOSAL_TYPE.UPDATE_ATTRIBUTE})/:phase(${phase})`,
        component: withPrivilegedAndSupplierPermission(['updateAttribute'])(BatchUpdateAttribute)
      })),
      // query batch proposals
      {
        pageKey: 'BatchProposalQuery',
        exact: true,
        path: '/proposal/batch/query',
        name: '查看批次修改提案',
        component: BatchProposalQuery
      }
    ]
  },
  {
    pageKey: 'KeywordQuery',
    exact: true,
    path: '/keyword/query',
    name: '關鍵字查詢',
    component: KeywordQuery,
    fetchData: KeywordQuery.fetchData
  },
  {
    pageKey: 'Dashboard',
    path: '/',
    component: Dashboard,
    fetchData: Dashboard.fetchData,
    exact: true
  },
  {
    pageKey: 'MobileDashboard',
    path: '/m/dashboard',
    component: MobileDashboard,
    fetchData: MobileDashboard.fetchData,
    exact: true
  },
  {
    pageKey: 'SellerHome',
    exact: true,
    path: '/seller',
    name: 'Seller Home',
    component: SellerHome,
    fetchData: SellerHome.fetchData
  },
  {
    pageKey: 'SellerLogin',
    exact: true,
    path: '/seller/login',
    name: 'Seller Login',
    component: SellerLogin,
    fetchData: SellerLogin.fetchData
  },
  {
    pageKey: 'SellerLogout',
    exact: true,
    path: '/seller/logout',
    name: 'Seller Logout',
    component: SellerLogout
  },
  {
    pageKey: 'SellerDashboard',
    exact: true,
    path: '/seller/dashboard',
    name: 'Seller Dashboard',
    component: SellerDashboard,
    fetchData: SellerDashboard.fetchData
  },
  {
    pageKey: 'SellerDashboard',
    exact: true,
    path: '/seller/dashboard',
    name: 'Seller Dashboard',
    component: SellerDashboard,
    fetchData: SellerDashboard.fetchData
  },
  {
    pageKey: 'SellerDashboardItemsBestSellerRanking',
    exact: true,
    path: '/seller/items/bestsellerRanking',
    name: 'Seller Dashboard Items Best Seller Ranking',
    component: SellerItemsBestSellerRanking,
    fetchData: SellerItemsBestSellerRanking.fetchData
  },
  {
    pageKey: 'SellerDashboardItemsSalesPerformance',
    exact: true,
    path: '/seller/items/salesPerformance',
    name: 'Seller Dashboard Items Sales Performance',
    component: SellerItemsSalesPerformance,
    fetchData: SellerItemsSalesPerformance.fetchData
  },
  ...dashboardRoutes.map(route => ({
    ...route,
    component: Dashboard,
    fetchData: Dashboard.fetchData,
    exact: true
  })),
  ...(
    isDevEnv
      ? [
        {
          exact: true,
          path: '/test',
          component: Test
        }
      ]
      : []
  ),
  {
    pageKey: 'ListingClone',
    exact: true,
    path: '/external/listing/clone',
    name: '複製賣場',
    component: ListingClone,
    fetchData: ListingClone.fetchData
  },
  {
    exact: true,
    path: '/seller/error',
    component: SellerError
  },
  {
    exact: true,
    path: '/error',
    component: ErrorPage
  }
]

export default routes
